import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReadAgreementDetail } from "@/schemas/agreement";
import { z } from "@/zod";
import DatePicker from "@/components/DatePicker";
import Field from "@/components/Field";
import Form from "@/components/Form";
import { DateTime } from "luxon";
import { DEFAULT_DATE_FORMAT } from "@/constants";

interface AgreementActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (date: string) => void;
  title: string;
  description: string;
  agreement?: ReadAgreementDetail;
}

interface FormData {
  date: string;
}

export const AgreementActionDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  agreement,
}: AgreementActionDialogProps) => {
  const { t } = useTranslation();

  const minDate = agreement?.agreement?.agreement_start_date;
  const maxDate = agreement?.agreement?.agreement_end_date;

  const schema = z.object({
    date: z
      .string()
      .date()
      .refine((val) => !minDate || val >= minDate, {
        message: t("agreements.errors.dateTooEarly", { minDate }),
      })
      .refine((val) => !maxDate || val <= maxDate, {
        message: t("agreements.errors.dateTooLate", { maxDate }),
      }),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: DateTime.now().toFormat(DEFAULT_DATE_FORMAT),
    },
  });

  const resetForm = useCallback(() => {
    form.reset({ date: DateTime.now().toFormat(DEFAULT_DATE_FORMAT) });
  }, [form]);

  const handleSubmit = useCallback(
    (data: FormData) => {
      onConfirm(data.date);
      resetForm();
    },
    [onConfirm, resetForm],
  );

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form form={form} handleSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <Field
              name="date"
              label={t("agreements.action.date")}
              required
              render={({ field }) => <DatePicker {...field} inDialog />}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} type="button">
              {t("common.cancel")}
            </Button>
            <Button type="submit">{t("agreements.action.confirm")}</Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
